import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { IconArrowLeft, IconPlus } from '@tabler/icons-react';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../../toolbar';
import {
  isTaskListReference,
  type TaskListDefinition,
  type TaskListReference,
  type TaskSectionDefinition,
  type TemplateTaskConfig,
} from '@techgitter/contracts';
import type { TicketTypeTemplate } from '../../../types/ticketTypeTemplate';
import { SectionEditor } from '../../task-lists/SectionEditor';
import { TaskListReferenceEditor } from './TaskListReferenceEditor';
import { slugify } from '../../../utils/slugify';
import { HelpHint } from '../HelpHint';
import { OUTLINED_BTN_SX } from '../../../styles/designSystem';
import '../EditorCard.css';

interface TemplateTaskConfigDetailEditorProps {
  config: TemplateTaskConfig;
  ticketTemplate: TicketTypeTemplate | undefined;
  taskLists: TaskListDefinition[];
  onChange: (updated: TemplateTaskConfig) => void;
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
}

export const TemplateTaskConfigDetailEditor: React.FC<TemplateTaskConfigDetailEditorProps> = ({
  config,
  ticketTemplate,
  taskLists,
  onChange,
  onBack,
  onSave,
  saving,
}) => {
  const sections = config.sections ?? [];

  const setSections = (next: Array<TaskSectionDefinition | TaskListReference>) => {
    onChange({ ...config, sections: next });
  };

  const updateSection = (index: number, updated: TaskSectionDefinition | TaskListReference) => {
    const next = [...sections];
    next[index] = updated;
    setSections(next);
  };

  const removeSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    setSections(next);
  };

  const addReference = () => {
    const ref: TaskListReference = {
      taskListId: taskLists[0]?.taskListId ?? '',
    };
    setSections([...sections, ref]);
  };

  const addInline = () => {
    const baseLabel = 'New Section';
    let candidateId = slugify(baseLabel);
    let n = 2;
    const existing = new Set(
      sections.filter((s): s is TaskSectionDefinition => !isTaskListReference(s)).map((s) => s.sectionId),
    );
    while (existing.has(candidateId)) {
      candidateId = `${slugify(baseLabel)}-${n++}`;
    }
    const inline: TaskSectionDefinition = {
      sectionId: candidateId,
      label: baseLabel,
      tasks: [],
    };
    setSections([...sections, inline]);
  };

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarButton icon={IconArrowLeft} onClick={onBack}>
          Back to list
        </ToolbarButton>
        <Box sx={{ ml: 0.5, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.2 }}>
            {ticketTemplate?.displayName ?? config.ticketTypeId}
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: '#888' }}>
            {config.ticketTypeId}
          </Typography>
        </Box>
        <ToolbarSpacer />
        <ToolbarButton variant="primary" onClick={onSave}>
          {saving ? 'Saving…' : 'Save'}
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: '2px',
            pt: '4px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span className="editor-section-title">Sections ({sections.length})</span>
            <HelpHint
              text={
                <Box sx={{ maxWidth: 320 }}>
                  The ordered list of sections that appears on tickets of this
                  type. Order matters — sections render top-to-bottom as written.
                  <br />
                  <br />
                  <strong>Reference:</strong> points at a reusable Task List
                  managed on the Task Lists page. Use when the same checklist
                  applies across multiple ticket types; edits to the source flow
                  through automatically.
                  <br />
                  <br />
                  <strong>Inline:</strong> defined right here. Use when the tasks
                  are unique to this ticket type and don't need to be reused.
                </Box>
              }
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<IconPlus size={16} stroke={1.5} />}
              onClick={addReference}
              title="Add a reference to a reusable Task List. The source list resolves at ticket-creation time, so updates to it flow through automatically."
              sx={OUTLINED_BTN_SX}
            >
              Add Reference
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<IconPlus size={16} stroke={1.5} />}
              onClick={addInline}
              title="Add an inline section unique to this ticket type."
              sx={OUTLINED_BTN_SX}
            >
              Add Inline Section
            </Button>
          </Box>
        </Box>

        {sections.length === 0 && (
          <div className="editor-empty">
            No sections yet. Add a reference to an existing task list or an inline section.
          </div>
        )}

        {sections.map((section, index) => {
          const isRef = isTaskListReference(section);
          const key = isRef
            ? `ref-${index}-${section.taskListId}`
            : `inline-${section.sectionId}`;
          if (isRef) {
            return (
              <TaskListReferenceEditor
                key={key}
                reference={section}
                taskLists={taskLists}
                onChange={(updated) => updateSection(index, updated)}
                onDelete={() => removeSection(index)}
                onMoveUp={index > 0 ? () => moveSection(index, 'up') : undefined}
                onMoveDown={
                  index < sections.length - 1 ? () => moveSection(index, 'down') : undefined
                }
              />
            );
          }
          return (
            <SectionEditor
              key={key}
              section={section}
              onSectionChange={(updated) => updateSection(index, updated)}
              onDelete={() => removeSection(index)}
              onMoveUp={index > 0 ? () => moveSection(index, 'up') : undefined}
              onMoveDown={
                index < sections.length - 1 ? () => moveSection(index, 'down') : undefined
              }
            />
          );
        })}
      </Box>
    </>
  );
};
